// Where a role's shell starts.
//
// ONE definition, used by two callers that must agree: App's post-login
// redirect, and the IA matrix's "Back to <role>" button. They were separate,
// and the button used "the first destination in the nav" as a stand-in for the
// landing — which for an agent is HOME, because Home is the first leaf in the
// rail. So "Back to Agent" returned an agent to a page that is not their desk
// and is not where signing in puts them.
//
// A landing is not derivable from nav order. It is a statement about what a
// role's day starts with, and the design says so per role (prototype/cc/roles.js
// gives each one a `landing`). Hence a lookup rather than a heuristic.

import { getActiveTenantSlug } from "./api";
import type { Me } from "./App";

export function landingFor(me: Me): string {
  const slug = getActiveTenantSlug();
  const m = me.memberships?.find((x) => x.slug === slug) ?? me.memberships?.[0];
  const roles = new Set(m?.roleSlugs ?? []);

  // A LIVE-DESK AGENT LANDS ON THE DESK. The design says so outright —
  // roles.js gives the Agent `landing: "desktop"` — and it is right: their
  // shift is the phone, and dropping them in an inbox costs a click at the
  // start of every one. It is also the only destination where being a click
  // late matters, because a queued caller is already waiting.
  if (roles.has("tenant-agent")) return "/voice/softphone";

  // Queue-working chairs land in the inbox; QA lands there too, because their
  // day is reading conversations.
  if (
    roles.has("tenant-user") ||
    roles.has("tenant-team-lead") ||
    roles.has("tenant-retention-specialist") ||
    roles.has("tenant-qa-analyst")
  ) {
    return "/conversations";
  }

  // Everyone else gets the role Home — which for them IS the useful first
  // screen: it says what they can reach, and their day is not one queue.
  return "/home";
}
